// src/hooks/useDiagnosticAutosave.js
import { useEffect, useRef, useCallback } from "react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebase";

const AUTOSAVE_INTERVAL_MS = 30_000; // 30s is plenty

export default function useDiagnosticAutosave({
  userUid,
  diagnosticId,
  estimatedMinutes,
  timeRemaining,
  disabled = false,
}) {
  // ===== REFS (SOURCE OF TRUTH) =====
  const timeRemainingRef = useRef(timeRemaining);
  const startedRef = useRef(false);

  // ===== KEEP REF SYNCED =====
  useEffect(() => {
    timeRemainingRef.current = timeRemaining;
  }, [timeRemaining]);

  // ===== COMPUTE TIME SPENT =====
  const computeTotalTimeSpent = () => {
    if (!estimatedMinutes) return 0;
    const total = estimatedMinutes * 60;
    return Math.max(total - (timeRemainingRef.current ?? total), 0);
  };

  // ===== SAVE NOW =====
  const saveNow = useCallback(
    async ({ sequence, responses } = {}) => {
      if (!userUid || !diagnosticId || disabled) return;

      const ref = doc(
        db,
        "users",
        userUid,
        "diagnosticAttempts",
        diagnosticId
      );

      const payload = {
        totalTimeSpentSeconds: computeTotalTimeSpent(),
        lastSavedAt: serverTimestamp(),
      };

      payload.status = "in_progress";

      if (!startedRef.current) {
        payload.startedAt = serverTimestamp();
        startedRef.current = true;
      }

      if (sequence) {
        payload.sequence = sequence;
        payload.currentIndex = sequence.length;
      }

      if (responses) {
        payload.responses = responses;
      }

      await updateDoc(ref, payload);
    },
    [userUid, diagnosticId, disabled, estimatedMinutes]
  );

  // ===== MARK COMPLETE =====
  const markComplete = useCallback(async () => {
    if (!userUid || !diagnosticId) return;

    const ref = doc(
      db,
      "users",
      userUid,
      "diagnosticAttempts",
      diagnosticId
    );

    await updateDoc(ref, {
      status: "completed",
      totalTimeSpentSeconds: computeTotalTimeSpent(),
      completedAt: serverTimestamp(),
      lastSavedAt: serverTimestamp(),
    });
  }, [userUid, diagnosticId, estimatedMinutes]);

  // ===== AUTOSAVE INTERVAL =====
  useEffect(() => {
    if (disabled || !userUid || !diagnosticId) return;

    const interval = setInterval(() => {
      saveNow();
    }, AUTOSAVE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [disabled, userUid, diagnosticId, saveNow]);

  return { saveNow, markComplete };
}
